import React, { useRef, useEffect } from 'react';
import './ApproveConfirmationModal.css';

interface ConfirmationModalProps {
  isOpen: boolean;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
  warningMessage?: string;
  isLoading?: boolean;
  /**
   * at-64 (BUG-V81): a checagem de pendências ainda está em curso. O modal abre antes de
   * ela responder, então sem isto havia uma janela em que dava para confirmar antes de o
   * aviso aparecer — quanto mais lenta a rede, maior a janela.
   */
  isVerifying?: boolean;
  /**
   * Aviso que NÃO bloqueia (ex: a checagem de pendências falhou). Separado de
   * `warningMessage` de propósito: falha de consulta não pode parar a aprovação
   * (mesmo princípio fail-open do at-48b), mas também não pode passar despercebida —
   * sem sinal, a ausência de aviso é lida como "não há nada pendente".
   */
  infoMessage?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  message,
  onConfirm,
  onCancel,
  sidebarRef,
  warningMessage,
  isLoading = false,
  isVerifying = false,
  infoMessage,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel, sidebarRef]);

  if (!isOpen) return null;

  return (
    <div className="approve-confirmation-modal-overlay">
      <div className="status-modal-content" ref={modalRef}>
        <div className="LightboxConfirm">
          <form className="LightboxStatusForm">
            <div className="EditarCarro">{message}</div>
            {warningMessage && (
              <div style={{ background: 'var(--tema-tinta-amarelo, #fef3c7)', border: '1px solid #f59e0b', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: 'var(--tema-texto-laranja, #92400e)', marginTop: '8px' }}>
                {warningMessage}
              </div>
            )}

            {infoMessage && !warningMessage && (
              <div style={{ background: 'var(--tema-fundo, #fdecea)', border: '1px solid #d93025', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: 'var(--tema-texto-vermelho, #a52714)', marginTop: '8px' }}>
                {infoMessage}
              </div>
            )}

            {isLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 0', gap: '10px' }}>
                <div className="spinner" />
                <span style={{ fontSize: '14px', color: 'var(--tema-texto-2, #666)', fontWeight: '500' }}>Confirmando...</span>
              </div>
            ) : (
              <div className="Frame1000003998Status">
                <div className="CtaBigEditar cancel" onClick={onCancel}>
                  <button className="CustomFileCancel" type="button">Cancelar</button>
                </div>
                <button
                  onClick={onConfirm}
                  className="CtaBigEditar save"
                  type="button"
                  disabled={!!warningMessage || isVerifying}
                  style={warningMessage || isVerifying ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                  title={isVerifying ? 'Aguarde: verificando pendências deste veículo.' : undefined}
                >
                  {isVerifying ? 'Verificando...' : 'Confirmar'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
